import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import {
  createInstitutionCategory,
  deleteInstitutionCategory,
  getInstitutionCategories,
  updateInstitutionCategory,
} from "../../services/institutionCategory";
import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import { categorySchema } from "../../validators/categoryValidators";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

const InstitutionCategory = () => {
  const [institutionCategories, setInstitutionCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = institutionCategories.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, institutionCategories]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const { data } = await getInstitutionCategories();
      setInstitutionCategories(data.data);
      setFilteredCategories(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (categoryToEdit) {
      onUpdateCategory(payload, resetForm);
    } else {
      onCreateCategory(payload, resetForm);
    }
  };

  const onCreateCategory = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createInstitutionCategory(payload);

      setInstitutionCategories((prev) => [...prev, data.data]);

      toast.success("Category created successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Category was not created, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setCategoryToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateCategory = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalCategories = [...institutionCategories];

      const categoryIndex = originalCategories.findIndex(
        (category) => category.id === categoryToEdit.id
      );

      if (categoryIndex === -1) throw new Error("Category not found.");

      const { data } = await updateInstitutionCategory(
        categoryToEdit.id,
        payload
      );

      originalCategories[categoryIndex] = data.data;

      setInstitutionCategories(originalCategories);

      setCategoryToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Category updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Category not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setCategoryToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteCategory = async () => {
    const originalCategories = [...institutionCategories];

    const newCategories = originalCategories.filter(
      (category) => category.id !== categoryToDelete.id
    );

    setInstitutionCategories(newCategories);

    try {
      await deleteInstitutionCategory(categoryToDelete?.id);

      toast.success("Category deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${response?.data?.message || error?.response?.data?.error || "Category not deleted, try again later"}`
      );

      setInstitutionCategories(originalCategories);
    } finally {
      setIsDeleteModalOpen(false);

      setCategoryToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setCategoryToEdit(null);
    setCategoryToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 300,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 400,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Category">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Category">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Institution Categories"
        subtitle1="Institution"
        subtitle2="Categories"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Category List"
            count={filteredCategories.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Category"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredCategories.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {categoryToEdit ? "Edit Category" : "Add Category"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: categoryToEdit ? categoryToEdit?.name : "",
                description: categoryToEdit ? categoryToEdit?.description : "",
              }}
              enableReinitialize
              validationSchema={categorySchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({ values, handleChange, handleBlur, errors, submitForm }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <TextInput
                      name="name"
                      title="Name"
                      placeholder="Enter Category"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoFocus
                    />

                    <TextInput
                      name="description"
                      title="Description"
                      placeholder="Enter Category Description"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteCategory}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default InstitutionCategory;
